import React, { useEffect, useState } from 'react';
import { moneyFormatter } from '../../helpers/utils';
import { useParams } from 'react-router-dom';

import TagService from '../../services/tag.service';
import ProductService from '../../services/product.service';
import AccountPlanService from '../../services/accountPlan.service';
import PropTypes from 'prop-types';
import MultipleSelectChip from '../subcomponents/MultipleSelectChip';
import 'react-quill/dist/quill.snow.css';

import {
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  InputAdornment,
  Button,
  Grid,
  InputLabel,
  FormControl,
  FormHelperText,
  Divider,
  Input
} from '@mui/material';

const paymentTypes = [
  {
    value: 'one_time',
    label: 'One Time'
  },
  {
    value: 'recurring',
    label: 'Subscription'
  }
];

const recurringIntervals = [
  {
    value: 'year',
    label: 'Year'
  },
  {
    value: 'month',
    label: 'Month'
  },
  {
    value: 'week',
    label: 'Week'
  },
  {
    value: 'day',
    label: 'Day'
  }
];

const afterCompletionTypes = [
  {
    value: 'hosted_confirmation',
    label: 'Hosted confirmation'
  },
  {
    value: 'redirect',
    label: 'Redirect'
  }
];

const currencies = [
  {
    value: 'COP',
    label: 'COP - Colombian Peso'
  },
  {
    value: 'EUR',
    label: 'EUR - Euro'
  },
  {
    value: 'PYG',
    label: 'PYG - Paraguayan Guarani'
  },
  {
    value: 'USD',
    label: 'USD - US Dollar'
  }
];

const currencySymbols = { USD: '$', EUR: '€', PYG: '₲', COP: '$' };
const currencySymbol = (currency) => currencySymbols[currency] || '$';

// Minimum product price per currency, in minor units. Keep in sync with the API's
// MIN_PRICES (app/utils/minimumPrice.js) and the webapp's minValues.
const minPrices = {
  USD: { amount: 300, message: 'Minimum price is 3$.' },
  EUR: { amount: 300, message: 'Minimum price is 3 EUR.' },
  PYG: { amount: 24000, message: 'Minimum price is 24000 PYG.' },
  COP: { amount: 13500, message: 'Minimum price is 13500 COP.' }
};

const ProductForm = ({ handleCloseModal, productId }) => {
  let params = useParams();
  let accountId = params.accountId;

  const initialProductState = {
    id: null,
    name: '',
    description: '',
    imageURL: '',
    price: 0,
    currency: 'USD',
    stripeProductId: '',
    stripePriceId: '',
    feePercentage: '',
    paymentType: 'one_time',
    recurringInterval: '',
    feeAmount: 0,
    afterCompletion: 'hosted_confirmation',
    redirectURL: '',
    accountId: null,
    shouldBePublished: true
  };

  const [product, setProduct] = useState(initialProductState);
  const [planFeePercent, setPlanFeePercent] = useState(null);
  // Fee currently stored on the product's payment link, shown next to the plan fee when editing.
  const [storedFee, setStoredFee] = useState(null);
  const [tags, setTags] = useState([]);
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // Mirrors the API's platform fee: the account plan may lower the percentage, but the
  // 2$ minimum under 20$ applies on every plan. Amounts are in cents.
  const calculateFee = (priceInCents, feePercent) => {
    const percent = feePercent || 10;
    if (priceInCents < 2000) {
      return { feeAmount: 200, feePercentage: (200 / (priceInCents / 100).toFixed(2)).toFixed(2) };
    }
    return { feeAmount: Math.floor(priceInCents * (percent / 100)), feePercentage: percent };
  };

  const handleInputChange = (event) => {
    let { name, value } = event.target;
    if (name === 'price') {
      value = value.replace(/[^\d]/g, '');
      setProduct({ ...product, price: moneyFormatter(value) });
    } else if (name === 'shouldBePublished') {
      value = event.target.checked;
      setProduct({ ...product, [name]: value });
    } else {
      setProduct({ ...product, [name]: value });
    }
  };

  const handleSelectChange = (valueArray) => {
    setProduct({ ...product, tags: valueArray });
  };

  const showAlert = (msg) => {
    setAlertMessage(msg);

    setTimeout(() => {
      setAlertMessage('');
    }, '6000');
    setIsSaving(false);
  };

  const getTags = () => {
    TagService.getAll()
      .then((response) => {
        setTags(response.data);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  useEffect(() => {
    async function loadParamsFirst() {
      await getTags();
    }

    loadParamsFirst();
  }, []);

  // The fee shown is always the one the account's current plan gives this price, also when
  // editing: the stored fee is what the product was created with, and saving re-applies the
  // plan fee on the API side. So a fee changed on the account shows up here right away.
  useEffect(() => {
    const priceInCents = Math.round(parseFloat(String(product.price).replace(/,/g, '')) * 100);
    if (isNaN(priceInCents) || priceInCents <= 0) {
      setProduct((prev) => ({ ...prev, feeAmount: 0, feePercentage: '' }));
      return;
    }
    const { feeAmount, feePercentage } = calculateFee(priceInCents, planFeePercent);
    setProduct((prev) => ({ ...prev, feeAmount: moneyFormatter(feeAmount), feePercentage: feePercentage }));
  }, [product.price, planFeePercent]);

  useEffect(() => {
    if (!accountId) return;
    AccountPlanService.getAccountPlan(accountId)
      .then((res) => {
        const feature = (res.data.accountPlan?.specs || []).find((s) => s.featureId == 1);
        setPlanFeePercent(feature?.volumeLimits?.feePercent || null);
      })
      .catch((e) => {
        console.log(e);
      });
  }, [accountId]);

  useEffect(() => {
    const getProduct = (productId) => {
      ProductService.get(accountId, productId)
        .then((response) => {
          let retrievedProduct = { ...response.data };

          retrievedProduct.tags = response.data.tags.map((tag) => {
            return tag.id;
          });

          const storedPaymentLink = response.data.price[0]?.paymentLink;
          setStoredFee(storedPaymentLink?.fee != null ? { amount: storedPaymentLink.fee, percentage: storedPaymentLink.feePercentage } : null);

          retrievedProduct.price = moneyFormatter(response.data.price[0]?.price);
          retrievedProduct.currency = response.data.price[0]?.currency.toUpperCase() || 'USD';

          retrievedProduct.paymentType = response.data.price[0]?.paymentType;
          retrievedProduct.recurringInterval = response.data.price[0]?.recurringInterval ? response.data.price[0]?.recurringInterval : '';

          retrievedProduct.afterCompletion = response.data.price[0]?.paymentLink?.afterCompletion;
          retrievedProduct.redirectURL = response.data.price[0]?.paymentLink?.redirectURL;

          setProduct(retrievedProduct);
        })
        .catch((e) => {
          console.log(e);
        });
    };

    if (productId && productId !== 'new' && productId !== '') {
      setEditing(true);
      getProduct(productId);
    }
  }, [accountId, productId]);

  const saveProduct = () => {
    setIsSaving(true);

    if (product.name === '') {
      showAlert('Name is mandatory.');
      return;
    }

    if (product.price === '' || product.price === 0) {
      showAlert('Price is mandatory.');
      return;
    }

    // Parse the formatted price (e.g. "1,500") into cents. The thousands separators must be
    // stripped first: `product.price * 100` is NaN for any value >= 1,000, and `NaN < minPrice`
    // is false, so large prices silently bypassed the minimum-price check below.
    const priceInCents = Math.round(parseFloat(String(product.price).replace(/,/g, '')) * 100);
    if (isNaN(priceInCents) || priceInCents <= 0) {
      showAlert('Price is mandatory.');
      return;
    }

    // Currency-specific minimum price validation
    const minPrice = minPrices[product.currency] || minPrices.USD;
    if (priceInCents < minPrice.amount) {
      showAlert(minPrice.message);
      return;
    }

    if (product.paymentType === 'recurring' && product.recurringInterval === '') {
      showAlert('Recurring Interval must be selected.');
      return;
    }

    if (product.afterCompletion === 'redirect' && product.redirectURL === '') {
      showAlert('A valid redirect URL must be provided.');
      return;
    }
    // TODO agregar control de tags

    var data = {
      name: product.name,
      description: product.description,
      imageURL: product.imageURL,
      price: priceInCents,
      currency: product.currency,
      paymentType: product.paymentType,
      recurringInterval: product.recurringInterval,
      afterCompletion: product.afterCompletion,
      redirectURL: product.redirectURL,
      tags: product.tags,
      accountId: accountId,
      shouldBePublished: product.shouldBePublished
    };

    if (editing) {
      ProductService.update(accountId, productId, data)
        .then(() => {
          handleCloseModal(true, true);
        })
        .catch((e) => {
          console.log(e);
          setIsSaving(false);
          showAlert('Error saving product.');
        });
    } else {
      ProductService.create(accountId, data)
        .then(() => {
          handleCloseModal(true, true);
        })
        .catch((e) => {
          console.log(e);
          setIsSaving(false);
          showAlert('Error saving product.');
        });
    }
  };

  const storedFeeDiffers =
    storedFee && (moneyFormatter(storedFee.amount) !== product.feeAmount || parseFloat(storedFee.percentage) !== parseFloat(product.feePercentage));

  return (
    <div>
      <Snackbar open={alertMessage !== ''}>
        <Alert severity="error">{alertMessage}</Alert>
      </Snackbar>
      <Grid container>
        <FormControl sx={{ width: '48%', mr: '2%' }}>
          <InputLabel htmlFor="name">Name</InputLabel>
          <Input name="name" id="name" aria-describedby="name" onChange={handleInputChange} value={product.name} />
        </FormControl>
      </Grid>
      <Grid container sx={{ mt: 5, mb: 4 }}>
        <FormControl sx={{ width: '100%', mb: 1 }}>
          <InputLabel htmlFor="description">Description</InputLabel>
          <Input
            name="description"
            id="description"
            multiline
            rows={4}
            value={product.description}
            onChange={(event) => {
              setProduct({ ...product, description: event.target.value });
            }}
            fullWidth
          />
        </FormControl>
      </Grid>
      <div>
        <FormControl sx={{ width: '32%', mr: '1%', mt: 2 }}>
          <InputLabel htmlFor="price">Price</InputLabel>
          <Input
            name="price"
            id="price"
            autoComplete="price"
            aria-describedby="Price"
            onChange={handleInputChange}
            value={product.price}
            startAdornment={<InputAdornment position="start">{currencySymbol(product.currency)}</InputAdornment>}
          />
        </FormControl>
        <FormControl sx={{ width: '16%', mr: '1%', mt: 2 }}>
          <InputLabel htmlFor="currency">Currency</InputLabel>
          <Select
            labelId="currency-label"
            id="currency"
            name="currency"
            value={product.currency}
            label="Currency"
            onChange={handleInputChange}
          >
            {currencies &&
              currencies.map((currency) => (
                <MenuItem key={currency.value} value={currency.value}>
                  {currency.value}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '49%', ml: '1%', mt: 2 }}>
          <InputLabel htmlFor="feePercentage">Fee Percentage</InputLabel>
          <Input
            name="feePercentage"
            id="feePercentage"
            disabled={true}
            aria-describedby="Fee Percentage"
            onChange={handleInputChange}
            value={product.feePercentage}
            startAdornment={<InputAdornment position="start">%</InputAdornment>}
          />
        </FormControl>
      </div>
      <div>
        <FormControl sx={{ width: '49%', mr: '1%', mt: 2 }}>
          <InputLabel htmlFor="feeAmount">Fee Amount</InputLabel>
          <Input
            name="feeAmount"
            id="feeAmount"
            disabled={true}
            aria-describedby="Fee Amount"
            value={product.feeAmount}
            startAdornment={<InputAdornment position="start">{currencySymbol(product.currency)}</InputAdornment>}
          />
          {editing && storedFee && (
            <FormHelperText>
              Current fee on this product: {moneyFormatter(storedFee.amount)} ({storedFee.percentage}%).
              {storedFeeDiffers ? ' Saving applies the fee above.' : ''}
            </FormHelperText>
          )}
        </FormControl>
      </div>
      <div>
        <FormControl sx={{ width: '49%', mr: '1%', mt: 2 }}>
          <InputLabel htmlFor="paymentType">Payment Type</InputLabel>
          <Select
            labelId="paymentType-label"
            id="paymentType"
            name="paymentType"
            value={product.paymentType}
            label="Payment Type"
            onChange={handleInputChange}
          >
            {paymentTypes &&
              paymentTypes.map((paymentType) => (
                <MenuItem key={paymentType.value} value={paymentType.value}>
                  {paymentType.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '49%', ml: '1%', mt: 2 }}>
          <InputLabel htmlFor="recurringInterval">Recurring Interval</InputLabel>
          <Select
            disabled={product.paymentType !== 'recurring'}
            labelId="recurringInterval-label"
            id="recurringInterval"
            name="recurringInterval"
            value={product.recurringInterval}
            label="Recurring Interval"
            onChange={handleInputChange}
          >
            {recurringIntervals &&
              recurringIntervals.map((recurringInterval) => (
                <MenuItem key={recurringInterval.value} value={recurringInterval.value}>
                  {recurringInterval.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </div>
      <Grid container>
        <FormControl sx={{ width: '49%', mr: '1%', mt: 2 }}>
          <InputLabel htmlFor="afterCompletion">After Completion Type</InputLabel>
          <Select
            labelId="afterCompletion-label"
            id="afterCompletion"
            name="afterCompletion"
            value={product.afterCompletion}
            label="After Completion Type"
            onChange={handleInputChange}
          >
            {afterCompletionTypes &&
              afterCompletionTypes.map((afterCompletionType) => (
                <MenuItem key={afterCompletionType.value} value={afterCompletionType.value}>
                  {afterCompletionType.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '49%', ml: '1%', mt: 2 }}>
          <InputLabel htmlFor="redirectURL">Redirect URL</InputLabel>
          <Input
            name="redirectURL"
            id="redirectURL"
            disabled={product.afterCompletion !== 'redirect'}
            aria-describedby="Redirect URL"
            onChange={handleInputChange}
            value={product.redirectURL}
          />
        </FormControl>
      </Grid>
      <Grid container>
        <FormControl sx={{ width: '49%', mr: '1%', mt: 2 }}>
          <MultipleSelectChip
            objs={tags}
            label={'Tags'}
            handleSelectChange={handleSelectChange}
            selected={product?.tags?.length > 0 ? product.tags : []}
          ></MultipleSelectChip>
        </FormControl>
        <FormControl sx={{ width: '49%', ml: '1%', mt: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={product.shouldBePublished} onChange={handleInputChange} name="shouldBePublished" color="primary" />}
            label="Publish on marketplace"
          />
        </FormControl>
      </Grid>
      <Divider sx={{ mt: 2, mb: 1 }} />
      {!isSaving ? (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button onClick={handleCloseModal} variant="contained" color="error">
            Cancel
          </Button>
          <Button onClick={saveProduct} variant="contained">
            Save
          </Button>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      )}
    </div>
  );
};
ProductForm.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
export default ProductForm;
